import { useCallback, useState, type MutableRefObject } from 'react';
import { Modal } from '@carbon/react';
import {
  uncheckWouldResetSurvivorWithDependents,
  type ConsolidationAction,
  type ConsolidationState,
} from './consolidation-utils';

// Shared §3.3 survivor-reset confirmation for both consolidation tearsheets. A
// left-panel deselect, a header select-all clear, or a right-panel trashcan that
// would empty a survivor with deduped names beneath it must confirm before
// resetting — otherwise the survivor and every folded name drop silently.
// Extracted so the entity-page and Match Review hosts can't drift: one had the
// guard on single-deselect but not select-all, the other had none at all.
export function useSurvivorResetConfirmation(
  dispatch: (action: ConsolidationAction) => void,
  stateRef: MutableRefObject<ConsolidationState>
) {
  // An array so a single removal and a "clear all" both flow through the one
  // confirmation, applied in order on confirm.
  const [pending, setPending] = useState<ConsolidationAction[] | null>(null);

  // Left-panel single check/uncheck.
  const toggle = useCallback(
    (id: number, selected: boolean) => {
      if (
        !selected &&
        uncheckWouldResetSurvivorWithDependents(stateRef.current, id)
      ) {
        setPending([{ type: 'uncheck', id }]);
        return;
      }
      dispatch({ type: selected ? 'check' : 'uncheck', id });
    },
    [dispatch, stateRef]
  );

  // Header select-all: check every selectable id, or clear them all — confirming
  // first when any would reset a survivor-with-dependents.
  const toggleAll = useCallback(
    (
      selectableIds: number[],
      selectedIds: Set<number>,
      allSelected: boolean
    ) => {
      if (allSelected) {
        const needsConfirm = selectableIds.some((id) =>
          uncheckWouldResetSurvivorWithDependents(stateRef.current, id)
        );
        if (needsConfirm) {
          setPending(selectableIds.map((id) => ({ type: 'uncheck', id })));
          return;
        }
        selectableIds.forEach((id) => dispatch({ type: 'uncheck', id }));
        return;
      }
      selectableIds.forEach((id) => {
        if (!selectedIds.has(id)) dispatch({ type: 'check', id });
      });
    },
    [dispatch, stateRef]
  );

  // Right-panel trashcan on a survivor-with-dependents.
  const requestRemoveName = useCallback(
    (key: string) => setPending([{ type: 'removeName', key }]),
    []
  );

  const confirm = useCallback(() => {
    pending?.forEach((action) => dispatch(action));
    setPending(null);
  }, [pending, dispatch]);

  const cancel = useCallback(() => setPending(null), []);

  return { pending, toggle, toggleAll, requestRemoveName, confirm, cancel };
}

// The identical Carbon confirmation both hosts render.
export function SurvivorResetModal({
  open,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      open={open}
      danger
      size="sm"
      modalHeading="Remove surviving entity?"
      primaryButtonText="Remove"
      secondaryButtonText="Cancel"
      onRequestClose={onCancel}
      onSecondarySubmit={onCancel}
      onRequestSubmit={onConfirm}
    >
      <p>
        This is the last remaining entity for this survivor. Removing it will
        reset the survivor and remove every deduplicated name grouped beneath
        it.
      </p>
    </Modal>
  );
}
